import { Link, NavLink } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import { ROUTES } from '@/constants'
import { cn } from '@/utils/cn'

/**
 * The coral category strip under the header. Top-level categories, each
 * opening a dropdown of its sub-categories on hover/focus, like the big BD
 * shops. Everything comes from the same categories the admin manages.
 *
 * Below 1280px the strip scrolls sideways instead of pushing the page wider
 * than the screen (with real category names it does not fit a 1024px iPad),
 * and the hover dropdowns are off — there is no hover on a touch screen, so
 * a tap just opens the category's page, where the shop filters list its
 * sub-categories. Phones get the same categories in the drawer menu instead
 * of a third header row.
 *
 * @param {{ tree: Array<{ id: string, name: string, children: any[] }> }} props
 */
export function CategoryNav({ tree }) {
  const linkClass = ({ isActive }) =>
    cn(
      'flex flex-shrink-0 items-center gap-1 whitespace-nowrap px-3 py-3 text-sm font-semibold transition-colors',
      isActive ? 'bg-white/20 text-white' : 'text-white/95 hover:bg-white/15 hover:text-white'
    )

  return (
    <nav aria-label="Categories" className="hidden bg-gradient-to-r from-coral-600 via-coral to-rose md:block">
      <div className="no-scrollbar mx-auto flex max-w-7xl items-center overflow-x-auto px-4 sm:px-6 lg:px-8 xl:overflow-visible">
        <NavLink to={ROUTES.shop} end className={linkClass}>
          All Products
        </NavLink>

        {tree.map((c) => {
          const to = `${ROUTES.shop}?category=${encodeURIComponent(c.id)}`
          if (c.children.length === 0) {
            return (
              <NavLink key={c.id} to={to} className={linkClass}>
                {c.name}
              </NavLink>
            )
          }
          return (
            <div key={c.id} className="group relative flex-shrink-0">
              <NavLink to={to} className={linkClass}>
                {c.name}
                <ChevronDown
                  size={14}
                  className="hidden transition-transform group-hover:rotate-180 group-focus-within:rotate-180 xl:block"
                />
              </NavLink>
              {/* Dropdown: sub-categories, with the parent itself on top. Desktop only. */}
              <div className="invisible absolute left-0 top-full z-30 hidden min-w-[220px] translate-y-1 rounded-b-xl border border-charcoal-100 bg-white py-2 opacity-0 shadow-modal transition group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100 xl:block">
                <Link
                  to={to}
                  className="block px-4 py-2 text-sm font-semibold text-charcoal hover:bg-sand-100 hover:text-coral"
                >
                  All {c.name}
                </Link>
                <div className="my-1 h-px bg-charcoal-100" />
                {c.children.map((sub) => (
                  <Link
                    key={sub.id}
                    to={`${ROUTES.shop}?category=${encodeURIComponent(sub.id)}`}
                    className="block px-4 py-2 text-sm text-charcoal-700 hover:bg-sand-100 hover:text-coral"
                  >
                    {sub.name}
                  </Link>
                ))}
              </div>
            </div>
          )
        })}

        <span className="mx-2 h-5 w-px flex-shrink-0 bg-white/30" />
        <NavLink to={ROUTES.about} className={linkClass}>About</NavLink>
        <NavLink to={ROUTES.contact} className={linkClass}>Contact</NavLink>
      </div>
    </nav>
  )
}
